import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Avatar,
  LinearProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Settings,
  Menu as MenuIcon,
  Logout,
  Info,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import logo from "../Images/BizBridge-Logo-1.png";
import { useDispatch, useSelector } from "react-redux";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const drawerWidth = 230;

const buyerSidebarItems = [
  {
    text: "Catalog",
    icon: <InventoryIcon />,
    route: "/buyer-dashboard/view-products",
  },
  {
    text: "My Orders",
    icon: <ShoppingCartIcon />,
    route: "/buyer-dashboard/my-orders",
  },
  {
    text: "Pending Invoices",
    icon: <ReceiptLongIcon />,
    route: "/buyer-dashboard/pending-invoices",
  },
  {
    text: "Dashboard",
    icon: <DashboardIcon />,
    route: "/buyer-dashboard",
  },
  {
    text: "Support",
    icon: <SupportAgentIcon />,
    route: "/buyer-dashboard/support",
  },
];

export default function DashboardLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSeller, setIsSeller] = useState(user?.mode === "seller");
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const profileCompletion = JSON.parse(
    localStorage.getItem("profileCompletion"),
  );

  // ── SELECT SELLER state ──────────────────────────────────────────────────
  const connectedSellers = useSelector(
    (state) => state.sellers?.connected || [],
  );
  const [selectedSeller, setSelectedSeller] = useState("");
  const [sellerMenuAnchor, setSellerMenuAnchor] = useState(null);
  const activeSeller = connectedSellers.find((s) => s._id === selectedSeller);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    setIsSeller(user?.mode === "seller");
  }, [user?.mode]);

  useEffect(() => {
    const isPathSeller = location.pathname
      .toLowerCase()
      .startsWith("/seller-dashboard");
    const isUserSeller = user?.mode === "seller";

    if (isPathSeller && !isUserSeller) {
      navigate("/buyer-dashboard/view-products", { replace: true });
    } else if (!isPathSeller && isUserSeller) {
      navigate("/seller-dashboard", { replace: true });
    }
  }, [location.pathname, user?.mode, navigate]);

  const handleProfileIconClick = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleOpenLogoutModal = () => {
    setOpenLogoutModal(true);
    setProfileMenuAnchor(null);
  };

  const handleCloseLogoutModal = () => setOpenLogoutModal(false);

  const handleLogout = () => {
    localStorage.clear();
    setOpenLogoutModal(false);
    window.location.reload("/login");
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return "#10b981";
    if (percentage >= 50) return "#f59e0b";
    return "#ef4444";
  };

  const getCompletionStatus = (percentage) => {
    if (percentage >= 80) return "Complete";
    if (percentage >= 50) return "Moderate";
    return "Low";
  };

  const currentSidebarItems = isSeller ? [] : buyerSidebarItems;

  const drawerContents = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Top Logo Section */}
      <Box
        component={Link}
        to={isSeller ? "/seller-dashboard" : "/seller-list"}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          py: 2,
          cursor: "pointer",
          textDecoration: "none",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={logo}
            sx={{
              height: 55,
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />
      <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
        {/* View Label */}
        <Typography
          sx={{
            mb: 2,
            fontWeight: 600,
            color: "#2e4254",
            fontSize: 15,
            letterSpacing: 0.5,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Buyer View
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography variant="caption" fontWeight={600} color="#64748b">
              Profile Completion
            </Typography>
            <Tooltip
              title={`${profileCompletion?.percentage || 0}% - ${getCompletionStatus(profileCompletion?.percentage || 0)}`}
            >
              <Info
                sx={{ fontSize: 14, color: "#94a3b8", cursor: "pointer" }}
              />
            </Tooltip>
          </Box>

          <Box sx={{ position: "relative" }}>
            <LinearProgress
              variant="determinate"
              value={profileCompletion?.percentage || 0}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "#e2e8f0",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: getCompletionColor(
                    profileCompletion?.percentage || 0,
                  ),
                  borderRadius: 3,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "0.7rem",
                textShadow: "0 1px 2px rgba(0,0,0,0.3)",
              }}
            >
              {profileCompletion?.percentage || 0}%
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0.5,
            }}
          >
            <Chip
              label={getCompletionStatus(profileCompletion?.percentage || 0)}
              size="small"
              sx={{
                backgroundColor:
                  getCompletionColor(profileCompletion?.percentage || 0) + "20",
                color: getCompletionColor(profileCompletion?.percentage || 0),
                fontWeight: 600,
                fontSize: "0.65rem",
                height: 20,
              }}
            />
            <Typography variant="caption" color="#94a3b8">
              {profileCompletion?.isProfileComplete
                ? "✓ Complete"
                : "Complete profile"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      {/* Menu Items List - Scrollable Section */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
        }}
      >
        <List sx={{ py: 0 }}>
          {currentSidebarItems.map(({ text, icon, route }) => {
            const isActive = location.pathname === route;
            return (
              <ListItem
                button
                key={text}
                selected={isActive}
                sx={{
                  py: 1,
                  mx: 1,
                  my: 0.5,
                  borderRadius: 2,
                  color: "#2e4254",
                  background: isActive ? "#f2f3f7" : "none",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: "#0b4064",
                    color: "#fff",
                    "& .MuiListItemIcon-root": { color: "#fff" },
                  },
                  fontWeight: isActive ? 700 : 600,
                  maxWidth: "100%",
                  overflowX: "hidden",
                }}
                onClick={() => {
                  navigate(route, { replace: true });
                  if (isMobile) setMobileOpen(false);
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? "#0b4064" : "#2e4254",
                    minWidth: 0,
                    pr: 1,
                    transition: "color 0.2s ease",
                  }}
                >
                  {icon}
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 700 : 600,
                    fontSize: 15,
                    letterSpacing: 0.2,
                    sx: {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Bottom User Info - Fixed at Bottom */}
      <Box
        sx={{
          mt: "auto",
          px: 2,
          py: 2,
          borderTop: "1px solid #ededed",
          display: { xs: "none", sm: "none", md: "flex" },
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
          background: "#fff",
          flexShrink: 0,
          minHeight: 70,
        }}
        onClick={() =>
          navigate(
            isSeller
              ? "/seller-dashboard/settings"
              : "/buyer-dashboard/settings",
            { replace: true },
          )
        }
      >
        <Avatar
          alt={user?.name}
          src={user?.profileImage?.url || "https://via.placeholder.com/60"}
          sx={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "#0b4064",
            color: "#fff",
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0,
            "& img": { borderRadius: "50%" },
          }}
        />
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#2e4254",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.name}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: "#7a869a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.businessName}
          </Typography>
        </Box>
        <IconButton size="small">
          <Settings sx={{ fontSize: 20, color: "#7a869a" }} />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        minWidth: 0,
        overflowX: "hidden",
        background: "#f2f3f7",
        boxSizing: "border-box",
      }}
    >
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: "#fff",
            borderRight: "1px solid #ededed",
            overflow: "hidden",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {drawerContents}
      </Drawer>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: "100vh",
          width: "100%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* ── TOP HEADER ─────────────────────────────────────────────────── */}
        <Box
          sx={{
            height: 60,
            background: "#fff",
            borderBottom: "1px solid #ededed",
            display: "flex",
            alignItems: "center",
            px: { xs: 2, sm: 4 },
            position: "sticky",
            top: 0,
            zIndex: 10,
            flexShrink: 0,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(true)}
              size="large"
              aria-label="open drawer"
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Mobile profile completion badge */}
          {isMobile && profileCompletion && (
            <Tooltip
              title={`Profile: ${profileCompletion.percentage}% ${profileCompletion.isProfileComplete ? "✓ Complete" : ""}`}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: 2,
                  p: 0.5,
                  cursor: "pointer",
                  borderRadius: 1,
                  backgroundColor:
                    getCompletionColor(profileCompletion.percentage) + "15",
                }}
                onClick={() => navigate("/buyer-dashboard/settings")}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: getCompletionColor(
                      profileCompletion.percentage,
                    ),
                    mr: 0.5,
                  }}
                />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color={getCompletionColor(profileCompletion.percentage)}
                >
                  {profileCompletion.percentage}%
                </Typography>
              </Box>
            </Tooltip>
          )}

          {/* ── SELECT SELLER DROPDOWN (replaces username + View As) ── */}
          <Button
            onClick={(e) => setSellerMenuAnchor(e.currentTarget)}
            endIcon={<KeyboardArrowDown sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#0b4064",
              fontSize: 14,
              px: 1.5,
              py: 0.75,
              borderRadius: 1.5,
              border: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              "&:hover": { backgroundColor: "#f1f5f9", borderColor: "#cbd5e1" },
              maxWidth: 220,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {activeSeller ? (
                <>
                  <Avatar
                    src={activeSeller.profileImage?.url}
                    alt={activeSeller.name}
                    sx={{ width: 22, height: 22, fontSize: 11 }}
                  />
                  <Typography
                    noWrap
                    sx={{ fontSize: 14, fontWeight: 600, color: "#0b4064" }}
                  >
                    {activeSeller.name}
                  </Typography>
                </>
              ) : (
                <Typography
                  sx={{ fontSize: 14, fontWeight: 600, color: "#64748b" }}
                >
                  Select Seller
                </Typography>
              )}
            </Box>
          </Button>

          <Menu
            anchorEl={sellerMenuAnchor}
            open={Boolean(sellerMenuAnchor)}
            onClose={() => setSellerMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 0.5,
                minWidth: 220,
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                border: "1px solid #e2e8f0",
              },
            }}
          >
            {connectedSellers.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="#94a3b8">
                  No connected sellers
                </Typography>
              </MenuItem>
            ) : (
              connectedSellers.map((seller) => (
                <MenuItem
                  key={seller._id}
                  selected={selectedSeller === seller._id}
                  onClick={() => {
                    setSelectedSeller(seller._id);
                    setSellerMenuAnchor(null);
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    "&.Mui-selected": { backgroundColor: "#eff6ff" },
                    "&:hover": { backgroundColor: "#f8fafc" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      src={seller.profileImage?.url}
                      alt={seller.name}
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: 12,
                        backgroundColor: "#0b4064",
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, color: "#2e4254" }}
                      >
                        {seller.name}
                      </Typography>
                      {seller.businessName && (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                          {seller.businessName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              ))
            )}
          </Menu>
          {/* ─────────────────────────────────────────────────────────── */}

          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop profile completion */}
          {!isMobile && profileCompletion && (
            <Tooltip
              title={`Profile Completion: ${profileCompletion.percentage}% - ${getCompletionStatus(profileCompletion.percentage)}. Click to complete.`}
            >
              <Button
                onClick={() => navigate("/buyer-dashboard/settings")}
                sx={{
                  mr: 2,
                  textTransform: "none",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor:
                    getCompletionColor(profileCompletion.percentage) + "15",
                  color: getCompletionColor(profileCompletion.percentage),
                  border: `1px solid ${getCompletionColor(profileCompletion.percentage)}30`,
                  "&:hover": {
                    backgroundColor:
                      getCompletionColor(profileCompletion.percentage) + "25",
                  },
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: getCompletionColor(
                      profileCompletion.percentage,
                    ),
                  }}
                />
                Profile: {profileCompletion.percentage}%
              </Button>
            </Tooltip>
          )}

          {/* Avatar */}
          <Avatar
            alt={user.name}
            src={user.profileImage?.url || "https://via.placeholder.com/60"}
            onClick={handleProfileIconClick}
            sx={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              background: "#0b4064",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              flexShrink: 0,
              cursor: "pointer",
              "& img": { borderRadius: "50%" },
            }}
          />

          {/* Profile dropdown menu */}
          <Menu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: "pointer",
                }}
                onClick={() =>
                  navigate(
                    isSeller
                      ? "/seller-dashboard/settings"
                      : "/buyer-dashboard/settings",
                    { replace: true },
                  )
                }
              >
                <Avatar
                  alt={user?.name}
                  src={
                    user?.profileImage?.url || "https://via.placeholder.com/60"
                  }
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: "#0b4064",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    flexShrink: 0,
                    "& img": { borderRadius: "50%" },
                  }}
                />
                <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#2e4254",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {user?.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#7a869a",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {user?.businessName}
                  </Typography>
                </Box>
                <IconButton size="small">
                  <Settings sx={{ fontSize: 20, color: "#7a869a" }} />
                </IconButton>
              </Box>
            </MenuItem>
            {/* <Divider sx={{ my: 1 }} />
            <MenuItem
              onClick={handleOpenLogoutModal}
              sx={{
                py: 1.5,
                px: 2,
                fontSize: 14,
                color: "#dc2626",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                "&:hover": { backgroundColor: "#fef2f2" },
              }}
            >
              <Logout sx={{ fontSize: 20, color: "#dc2626" }} />
              Logout
            </MenuItem> */}
          </Menu>
        </Box>
        {/* ── END HEADER ─────────────────────────────────────────────────── */}

        {/* Logout Dialog */}
        <Dialog open={openLogoutModal} onClose={handleCloseLogoutModal}>
          <DialogTitle>Logout</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to logout?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLogoutModal}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={handleLogout}>
              Logout
            </Button>
          </DialogActions>
        </Dialog>

        {/* Main Content */}
        <Box
          sx={{
            p: { xs: 1, sm: 2, md: 4 },
            width: "100%",
            mx: "auto",
            minHeight: "calc(100vh - 60px)",
            boxSizing: "border-box",
            overflowY: "auto",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
